import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { db } from '@/lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { LogOut, Users, DollarSign, Settings, Copy, ExternalLink, Activity, Plus, ShieldCheck, Music, Sparkles, Radio, Smartphone, PlayCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { formatMoney, getCurrencyForCountry } from '@/lib/currency';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Link } from 'react-router-dom';

const AgentDashboardPage = () => {
  const { user, profile, signOut, updateProfileData } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDemoData, setShowDemoData] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  
  const [payoutBankName, setPayoutBankName] = useState(profile?.bank_name || '');
  const [payoutAccountNumber, setPayoutAccountNumber] = useState(profile?.account_number || '');
  const [payoutSortCode, setPayoutSortCode] = useState(profile?.sort_code || '');
  const [payoutName, setPayoutName] = useState(profile?.account_holder_name || '');
  const [paypalEmail, setPaypalEmail] = useState(profile?.agent_paypal_email || '');
  const [venmoHandle, setVenmoHandle] = useState(profile?.agent_venmo_handle || '');

  useEffect(() => {
    if (!profile) return;
    setPayoutBankName(profile.bank_name || '');
    setPayoutAccountNumber(profile.account_number || '');
    setPayoutSortCode(profile.sort_code || '');
    setPayoutName(profile.account_holder_name || '');
    setPaypalEmail(profile.agent_paypal_email || '');
    setVenmoHandle(profile.agent_venmo_handle || '');
    if (profile.account_type === 'agent' && !profile.agent_onboarding_completed) {
      setShowOnboardingWizard(true);
    }
  }, [profile]);

  const fetchArtists = async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, 'profiles'),
        where('agent_id', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      const fetchedArtists = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setArtists(fetchedArtists);
    } catch (err) {
      console.error("Error fetching artists:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtists();
  }, [user]);

  const handleCopyLink = () => {
    const inviteLink = `${window.location.origin}/signup?agent=${profile?.agent_code || ''}`;
    navigator.clipboard.writeText(inviteLink);
    toast({
      title: "Invite Link Copied!",
      description: "Send this link to your artists so they can sign up under your agency."
    });
  };

  const handleSavePayoutSettings = async () => {
    try {
      const isUs = (profile?.country || '').toUpperCase().slice(0, 2) === 'US';
      if (isUs) {
        await updateProfileData(user.uid, {
          account_holder_name: payoutName,
          agent_paypal_email: paypalEmail.trim() || null,
          agent_venmo_handle: venmoHandle.trim() || null,
        });
        toast({
          title: "Payout Settings Saved",
          description: "Your PayPal and Venmo details have been updated."
        });
      } else {
        await updateProfileData(user.uid, {
          bank_name: payoutBankName,
          account_number: payoutAccountNumber,
          sort_code: payoutSortCode,
          account_holder_name: payoutName
        });
        toast({
          title: "Payout Settings Saved",
          description: "Your bank details have been updated."
        });
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error saving settings",
        description: err.message
      });
    }
  };

  const handleRequestPayout = async () => {
    const isUs = (profile?.country || '').toUpperCase().slice(0, 2) === 'US';
    if (isUs) {
      const hasPaypal = paypalEmail.trim().length > 3 && paypalEmail.includes('@');
      const hasVenmo = venmoHandle.trim().length > 1;
      if (!hasPaypal && !hasVenmo) {
        toast({
          variant: "destructive",
          title: "Missing payout details",
          description: "Please enter at least one of PayPal email or Venmo handle before requesting a payout."
        });
        return;
      }
    } else {
      if (!payoutAccountNumber || !payoutSortCode) {
        toast({
          variant: "destructive",
          title: "Missing Bank Details",
          description: "Please fill out your account number and sort code before requesting a withdrawal."
        });
        return;
      }
    }

    toast({
      title: "Payout Requested",
      description: "Your payout request has been submitted to the admin team for review."
    });
  };

  const handleChangeDefaultSplit = async (preference) => {
    try {
      await updateProfileData(user.uid, {
        default_payout_preference: preference
      });
      toast({
        title: "Split Preference Updated",
        description: `Your default split preference is now set to ${preference === '80_20' ? 'Agent Managed (80/20)' : 'Auto Split (60/20/20)'}.`
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error updating preference",
        description: err.message
      });
    }
  };

  const completeOnboardingWizard = async () => {
    setShowOnboardingWizard(false);
    if (!user?.uid) return;
    try {
      await updateProfileData(user.uid, {
        agent_onboarding_completed: true,
        agent_onboarding_completed_at: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error completing agent onboarding:', err);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">Loading...</div>;
  }

  const currentSplit = profile?.default_payout_preference || '80_20';
  const totalDeferredPayments = (profile?.deferred_payments || 0) / 100;
  const agentCountryCode = (profile?.country || 'GB').toUpperCase().slice(0, 2);
  const isUsAgent = agentCountryCode === 'US';
  const payoutCurrency = profile?.currency || getCurrencyForCountry(agentCountryCode);
  const fmtBalance = (amount) => formatMoney(amount, payoutCurrency);

  const demoBalanceAmount = 316;
  const demoArtists = isUsAgent
    ? [
        { id: 'demo-us-1', artist_name: 'The Riverside Band', email: 'bookings@riverside-band.demo', agent_payout_preference: '80_20', is_live: true, url_slug: null, demoSessionGross: 98, demoSessionRequests: 22 },
        { id: 'demo-us-2', artist_name: 'Maya Stone', email: 'maya.stone@demo.com', agent_payout_preference: '60_20_20', is_live: false, url_slug: null, demoSessionGross: 85, demoSessionRequests: 19 },
        { id: 'demo-us-3', artist_name: 'Jordan & Co.', email: 'hello@jordanco.demo', agent_payout_preference: '80_20', is_live: false, url_slug: null, demoSessionGross: 73, demoSessionRequests: 16 },
        { id: 'demo-us-4', artist_name: 'Northside Acoustic', email: 'hello@northsideacoustic.demo', agent_payout_preference: '60_20_20', is_live: false, url_slug: null, demoSessionGross: 60, demoSessionRequests: 14 },
      ]
    : [
        { id: 'demo-gb-1', artist_name: 'The Night Owls', email: 'contact@nightowls.demo', agent_payout_preference: '80_20', is_live: true, url_slug: null, demoSessionGross: 98, demoSessionRequests: 22 },
        { id: 'demo-gb-2', artist_name: 'Ella James Live', email: 'bookings@ellajames.demo', agent_payout_preference: '60_20_20', is_live: false, url_slug: null, demoSessionGross: 85, demoSessionRequests: 19 },
        { id: 'demo-gb-3', artist_name: 'Harbour Lights', email: 'info@harbourlights.demo', agent_payout_preference: '80_20', is_live: false, url_slug: null, demoSessionGross: 73, demoSessionRequests: 16 },
        { id: 'demo-gb-4', artist_name: 'Copper Row', email: 'bookings@copperrow.demo', agent_payout_preference: '60_20_20', is_live: false, url_slug: null, demoSessionGross: 60, demoSessionRequests: 14 },
      ];

  const demoActivityLines = isUsAgent
    ? [
        { label: 'Paid song request', detail: 'The Riverside Band', amount: 6 },
        { label: 'Paid song request', detail: 'Maya Stone', amount: 5.5 },
        { label: 'Tip', detail: 'The Riverside Band', amount: 4 },
        { label: 'Paid song request', detail: 'Jordan & Co.', amount: 7 },
      ]
    : [
        { label: 'Paid song request', detail: 'The Night Owls', amount: 6 },
        { label: 'Paid song request', detail: 'Ella James Live', amount: 5.5 },
        { label: 'Tip', detail: 'Harbour Lights', amount: 4 },
        { label: 'Paid song request', detail: 'Copper Row', amount: 7 },
      ];

  const splitDemoSessionMoney = (preference, gross) => {
    const g = Number(gross) || 0;
    if (preference === '80_20') {
      return { artistShare: g * 0.2, agentShare: g * 0.8, platformShare: 0 };
    }
    return { artistShare: g * 0.6, agentShare: g * 0.2, platformShare: g * 0.2 };
  };

  const totalArtistCount = artists.length;
  const displayBalance = showDemoData ? demoBalanceAmount : totalDeferredPayments;
  const displayArtistCount = showDemoData ? demoArtists.length : totalArtistCount;
  const rosterToShow = showDemoData ? demoArtists : artists;

  return (
    <div className="min-h-screen bg-black text-gray-200">
      <Helmet>
        <title>Agent Dashboard - Your Song Request</title>
      </Helmet>

      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-white tracking-tight">YSR <span className="text-blue-400">Agent</span></span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400 hidden sm:block">{profile?.artist_name || profile?.full_name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowOnboardingWizard(true)}
              className="hidden sm:inline-flex text-blue-300 hover:text-white hover:bg-blue-900/30"
            >
              <PlayCircle className="w-4 h-4 mr-2" /> How YSR works
            </Button>
            <Button variant="ghost" size="sm" onClick={signOut} className="text-gray-400 hover:text-white hover:bg-gray-800">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row gap-8">
        {/* Sidebar Navigation */}
        <aside className="w-full md:w-64 flex-shrink-0">
          <nav className="space-y-2">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'overview' ? 'bg-blue-600/20 text-blue-400 font-medium border border-blue-500/30' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
            >
              <Activity className="w-5 h-5" /> Overview
            </button>
            <button 
              onClick={() => setActiveTab('artists')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'artists' ? 'bg-blue-600/20 text-blue-400 font-medium border border-blue-500/30' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
            >
              <Users className="w-5 h-5" /> My Roster
            </button>
            <button 
              onClick={() => setActiveTab('payouts')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'payouts' ? 'bg-blue-600/20 text-blue-400 font-medium border border-blue-500/30' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
            >
              <DollarSign className="w-5 h-5" /> Payouts & Banking
            </button>
            <button 
              onClick={() => setActiveTab('settings')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === 'settings' ? 'bg-blue-600/20 text-blue-400 font-medium border border-blue-500/30' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
            >
              <Settings className="w-5 h-5" /> Preferences
            </button>
          </nav>

          <div className="mt-8 bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">Your Invite Link</p>
            <div className="bg-black border border-gray-700 rounded-lg p-2 flex items-center justify-between mb-3">
              <span className="text-xs text-gray-300 truncate w-full text-left">.../signup?agent={profile?.agent_code}</span>
            </div>
            <Button onClick={handleCopyLink} size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs">
              <Copy className="w-3.5 h-3.5 mr-2" /> Copy Link
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={() => setShowOnboardingWizard(true)}
            className="mt-4 w-full border-blue-700/60 text-blue-200 hover:bg-blue-950/40 hover:text-white"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Watch YSR Demo
          </Button>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white">Dashboard Overview</h1>
                  <p className="text-gray-400">Welcome back, {profile?.full_name}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="border-amber-600/50 text-amber-200 hover:bg-amber-950/40 hover:text-amber-100 shrink-0"
                  onClick={() => setShowDemoData((v) => !v)}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {showDemoData ? 'Hide demo data' : 'See demo data'}
                </Button>
              </div>

              {showDemoData && (
                <p className="text-sm text-amber-200/90 bg-amber-950/30 border border-amber-700/40 rounded-lg px-4 py-3">
                  Sample numbers and activity only ({payoutCurrency === 'USD' ? '$ USD' : '£ GBP'}). Turn off demo data to see your real dashboard.
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="bg-gray-900 border-gray-800">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-gray-400 font-medium">Available Balance</h3>
                      <div className="w-10 h-10 rounded-full bg-green-900/30 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">{fmtBalance(displayBalance)}</div>
                    <p className="text-sm text-gray-500">Earnings ready to be withdrawn</p>
                    {displayBalance > 0 && (
                      <Button
                        onClick={() => setActiveTab('payouts')}
                        disabled={showDemoData}
                        className="mt-4 w-full bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:pointer-events-none"
                        title={showDemoData ? 'Switch off demo data to use payouts with your real balance' : undefined}
                      >
                        Request Payout
                      </Button>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-gray-900 border-gray-800">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-gray-400 font-medium">Active Roster</h3>
                      <div className="w-10 h-10 rounded-full bg-blue-900/30 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-400" />
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">{displayArtistCount}</div>
                    <p className="text-sm text-gray-500">Artists linked to your agency</p>
                    <Button variant="outline" onClick={() => setActiveTab('artists')} className="mt-4 w-full border-gray-700 hover:bg-gray-800 text-white">
                      View Roster
                    </Button>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="bg-gray-900 border-gray-800 mt-6">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {showDemoData ? (
                    <ul className="space-y-3">
                      {demoActivityLines.map((line, i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between gap-3 py-3 px-4 rounded-lg bg-black/40 border border-gray-800"
                        >
                          <div>
                            <p className="text-sm text-white font-medium">{line.label}</p>
                            <p className="text-xs text-gray-500">{line.detail}</p>
                          </div>
                          <span className="text-sm font-semibold text-green-400 tabular-nums">{fmtBalance(line.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Activity className="w-8 h-8 mx-auto mb-3 opacity-20" />
                      <p>Once your artists start receiving paid requests, their activity will appear here.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'artists' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white">My Roster</h1>
                  <p className="text-gray-400">Manage artists connected to your agency</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-amber-600/50 text-amber-200 hover:bg-amber-950/40 hover:text-amber-100"
                    onClick={() => setShowDemoData((v) => !v)}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {showDemoData ? 'Hide demo data' : 'See demo data'}
                  </Button>
                  <Button onClick={handleCopyLink} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Invite Artist
                  </Button>
                </div>
              </div>

              {showDemoData && (
                <p className="text-sm text-amber-200/90 bg-amber-950/30 border border-amber-700/40 rounded-lg px-4 py-3">
                  Sample roster ({payoutCurrency === 'USD' ? '$ USD' : '£ GBP'}). Gross figures are illustrative. Turn off demo data to see your real artists.
                </p>
              )}

              {!showDemoData && artists.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center">
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">No artists yet</h3>
                  <p className="text-gray-400 max-w-md mx-auto mb-6">
                    Share your invite link with your artists. Once they sign up, they'll appear here and you'll automatically receive a cut of their earnings.
                  </p>
                  <Button onClick={handleCopyLink} variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
                    <Copy className="w-4 h-4 mr-2" /> Copy Invite Link
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {rosterToShow.map((artist) => {
                    const demoGlance =
                      showDemoData && typeof artist.demoSessionGross === 'number'
                        ? splitDemoSessionMoney(artist.agent_payout_preference, artist.demoSessionGross)
                        : null;
                    return (
                      <div key={artist.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center overflow-hidden border border-gray-700">
                              {artist.profile_image_url ? (
                                <img src={artist.profile_image_url} alt={artist.artist_name} className="w-full h-full object-cover" />
                              ) : (
                                <Music className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                                {artist.artist_name}
                                {artist.is_live && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>}
                              </h4>
                              <p className="text-sm text-gray-400">{artist.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 sm:gap-8">
                            <div className="text-right">
                              <p className="text-xs text-gray-500 uppercase font-semibold">Split</p>
                              <p className="text-sm text-blue-400 font-medium">
                                {artist.agent_payout_preference === '80_20' ? '80% Agent' : '20% Agent / 60% Artist'}
                              </p>
                            </div>
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-gray-500 uppercase font-semibold">Status</p>
                              <p className="text-sm text-white">
                                {artist.is_live ? <span className="text-red-400">Live Now</span> : 'Offline'}
                              </p>
                            </div>
                            {artist.url_slug ? (
                              <Link to={`/${artist.url_slug}`} target="_blank" className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
                                <ExternalLink className="w-5 h-5 text-gray-400" />
                              </Link>
                            ) : (
                              <span className="p-2 text-gray-600" title="Demo artist">
                                <ExternalLink className="w-5 h-5" />
                              </span>
                            )}
                          </div>
                        </div>
                        {demoGlance && (
                          <div className="rounded-lg border border-amber-800/30 bg-black/40 px-4 py-3 space-y-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-amber-200/90">Recent sessions (sample)</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                              <div>
                                <p className="text-xs text-gray-500">Paid requests</p>
                                <p className="font-semibold text-white tabular-nums">{artist.demoSessionRequests ?? '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Gross (demo)</p>
                                <p className="font-semibold text-white tabular-nums">{fmtBalance(artist.demoSessionGross)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Artist share</p>
                                <p className="font-semibold text-cyan-300 tabular-nums">{fmtBalance(demoGlance.artistShare)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Agent share</p>
                                <p className="font-semibold text-blue-300 tabular-nums">{fmtBalance(demoGlance.agentShare)}</p>
                              </div>
                            </div>
                            {artist.agent_payout_preference === '60_20_20' && demoGlance.platformShare > 0 && (
                              <p className="text-xs text-gray-500">
                                Platform (demo): {fmtBalance(demoGlance.platformShare)} on this split
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'payouts' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-bold text-white">Payouts & Banking</h1>
                <p className="text-gray-400">
                  {isUsAgent
                    ? 'Add PayPal and/or Venmo so we can pay you manually. USD balance.'
                    : 'Add your UK bank details (account number and sort code) so we can pay you manually.'}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-gray-900 border-gray-800">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Withdraw Funds</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-black/50 border border-gray-800 rounded-xl p-6 text-center mb-6">
                      <p className="text-gray-400 text-sm mb-2">Available to Withdraw</p>
                      <p className="text-4xl font-bold text-green-400 mb-4">{fmtBalance(displayBalance)}</p>
                      <Button 
                        onClick={handleRequestPayout} 
                        disabled={displayBalance <= 0 || showDemoData}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-6 rounded-xl disabled:opacity-50 disabled:pointer-events-none"
                        title={showDemoData ? 'Switch off demo data to request a payout against your real balance' : undefined}
                      >
                        Request Payout
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      {isUsAgent
                        ? 'Payouts are processed manually to the PayPal or Venmo details you save. Typical turnaround is a few business days.'
                        : 'Payouts are processed manually to the UK bank details you specify. Typical turnaround is 3-5 business days.'}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900 border-gray-800">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex justify-between items-center">
                      {isUsAgent ? 'PayPal & Venmo' : 'UK bank details'}
                      <ShieldCheck className="w-5 h-5 text-gray-500" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleSavePayoutSettings(); }}>
                      <div className="space-y-2">
                        <Label className="text-gray-300">{isUsAgent ? 'Legal / payee name' : 'Account holder name'}</Label>
                        <Input 
                          value={payoutName}
                          onChange={(e) => setPayoutName(e.target.value)}
                          placeholder={isUsAgent ? 'Name on PayPal or Venmo' : 'e.g. Prestige Management Ltd'}
                          className="bg-black border-gray-700 text-white"
                        />
                      </div>
                      {isUsAgent ? (
                        <>
                          <div className="space-y-2">
                            <Label className="text-gray-300">PayPal email</Label>
                            <Input 
                              type="email"
                              value={paypalEmail}
                              onChange={(e) => setPaypalEmail(e.target.value)}
                              placeholder="you@email.com"
                              className="bg-black border-gray-700 text-white"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-300">Venmo handle</Label>
                            <Input 
                              value={venmoHandle}
                              onChange={(e) => setVenmoHandle(e.target.value)}
                              placeholder="@YourVenmo or username"
                              className="bg-black border-gray-700 text-white"
                            />
                          </div>
                          <p className="text-xs text-gray-500">Provide at least one of PayPal or Venmo so we can send your balance.</p>
                        </>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label className="text-gray-300">Bank name</Label>
                            <Input 
                              value={payoutBankName}
                              onChange={(e) => setPayoutBankName(e.target.value)}
                              placeholder="e.g. Barclays"
                              className="bg-black border-gray-700 text-white"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-gray-300">Account number</Label>
                              <Input 
                                value={payoutAccountNumber}
                                onChange={(e) => setPayoutAccountNumber(e.target.value)}
                                placeholder="8 digits"
                                className="bg-black border-gray-700 text-white font-mono"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-300">Sort code</Label>
                              <Input 
                                value={payoutSortCode}
                                onChange={(e) => setPayoutSortCode(e.target.value)}
                                placeholder="XX-XX-XX"
                                className="bg-black border-gray-700 text-white font-mono"
                              />
                            </div>
                          </div>
                        </>
                      )}
                      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-2">
                        {isUsAgent ? 'Save PayPal / Venmo' : 'Save bank details'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-bold text-white">Preferences</h1>
                <p className="text-gray-400">Configure how your agency operates on YSR</p>
              </div>

              <Card className="bg-gray-900 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Default Payout Split</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-400 mb-6">
                    This determines how revenue is split for <span className="font-bold text-white">new artists</span> that sign up using your link.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => handleChangeDefaultSplit('80_20')}
                      className={`p-5 rounded-xl border text-left flex flex-col transition-all ${
                        currentSplit === '80_20' 
                        ? 'bg-blue-900/30 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.15)]' 
                        : 'bg-black/40 border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-base font-bold ${currentSplit === '80_20' ? 'text-blue-400' : 'text-gray-300'}`}>Agent Managed (80/20)</span>
                        {currentSplit === '80_20' && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                      </div>
                      <span className="text-sm text-gray-400">You receive 80% of gross revenue to your YSR account. Artists receive 0% from YSR (you pay them directly off-platform).</span>
                    </button>
                    
                    <button
                      onClick={() => handleChangeDefaultSplit('60_20_20')}
                      className={`p-5 rounded-xl border text-left flex flex-col transition-all ${
                        currentSplit === '60_20_20' 
                        ? 'bg-purple-900/30 border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                        : 'bg-black/40 border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-base font-bold ${currentSplit === '60_20_20' ? 'text-purple-400' : 'text-gray-300'}`}>Auto Split (60/20/20)</span>
                        {currentSplit === '60_20_20' && <div className="w-2 h-2 rounded-full bg-purple-500"></div>}
                      </div>
                      <span className="text-sm text-gray-400">You receive 20% of gross revenue. The artist automatically receives 60% of gross revenue to their own YSR account.</span>
                    </button>
                  </div>
                  
                  <div className="mt-6 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg flex gap-3">
                    <ShieldCheck className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <p className="text-xs text-yellow-200">
                      Changing this preference only affects <strong>future</strong> artists who sign up using your link. Existing artists' splits remain unaffected. To change a split for an existing artist, please contact support.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>

      <AnimatePresence>
        {showOnboardingWizard && (
          <AgentOnboardingWizard
            agentName={profile?.artist_name || profile?.full_name || 'your agency'}
            inviteCode={profile?.agent_code || ''}
            currency={payoutCurrency}
            fmtBalance={fmtBalance}
            onClose={() => setShowOnboardingWizard(false)}
            onComplete={completeOnboardingWizard}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const wizardSteps = [
  {
    eyebrow: 'Step 1',
    title: 'Your agent link brings artists into YSR',
    body: 'You share one tracked link with your roster. Any artist who signs up from it is permanently connected to your Agent account.',
    cta: 'Next: what the artist sees'
  },
  {
    eyebrow: 'Step 2',
    title: 'The artist goes live and fans send requests',
    body: 'Artists build a catalogue, go live at a gig, and fans scan their QR code to request songs from their phone.',
    cta: 'Next: paid request demo'
  },
  {
    eyebrow: 'Step 3',
    title: 'Paid requests appear in the live queue',
    body: 'When the fan pays, the request appears in the artist’s incoming feed so they can play it, skip it, or keep the crowd moving.',
    cta: 'Next: your Agent dashboard'
  },
  {
    eyebrow: 'Step 4',
    title: 'You monitor roster performance and payouts',
    body: 'Your dashboard shows linked artists, sample revenue split behaviour, and the payout balance your agency can withdraw.',
    cta: 'Finish tour'
  }
];

const AgentOnboardingWizard = ({ agentName, inviteCode, currency, fmtBalance, onClose, onComplete }) => {
  const [step, setStep] = useState(0);
  const current = wizardSteps[step];
  const isLast = step === wizardSteps.length - 1;

  const next = () => {
    if (isLast) {
      onComplete();
      return;
    }
    setStep((s) => s + 1);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative w-full max-w-6xl overflow-hidden rounded-3xl border border-blue-500/30 bg-[#050816] shadow-[0_0_80px_rgba(37,99,235,0.28)]"
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }}
      >
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-purple-600/20 blur-3xl" />

        <div className="relative grid min-h-[680px] grid-cols-1 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="flex flex-col justify-between border-b border-blue-500/20 p-6 sm:p-8 lg:border-b-0 lg:border-r">
            <div>
              <div className="mb-6 inline-flex items-center rounded-full border border-blue-400/30 bg-blue-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-blue-200">
                Agent onboarding
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 18 }}
                  transition={{ duration: 0.22 }}
                >
                  <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">{current.eyebrow}</p>
                  <h2 className="text-3xl font-black leading-tight text-white sm:text-4xl">
                    {current.title}
                  </h2>
                  <p className="mt-5 text-base leading-7 text-gray-300">
                    {current.body}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-8">
              <div className="mb-5 flex gap-2">
                {wizardSteps.map((_, i) => (
                  <div
                    key={i}
                    className={`h-2 flex-1 rounded-full transition-colors ${
                      i <= step ? 'bg-blue-400' : 'bg-gray-800'
                    }`}
                  />
                ))}
              </div>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  className="text-gray-400 hover:bg-gray-900 hover:text-white"
                >
                  Close for now
                </Button>
                <Button
                  type="button"
                  onClick={next}
                  className="bg-blue-600 px-6 text-white hover:bg-blue-500"
                >
                  {current.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="relative min-h-[420px] overflow-hidden p-4 sm:p-8">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_40%),radial-gradient(circle_at_bottom_right,_rgba(168,85,247,0.16),_transparent_38%)]" />
            <div className="relative h-full">
              <AnimatePresence mode="wait">
                <WizardVisual
                  key={step}
                  step={step}
                  agentName={agentName}
                  inviteCode={inviteCode}
                  currency={currency}
                  fmtBalance={fmtBalance}
                />
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const WizardVisual = ({ step, agentName, inviteCode, fmtBalance }) => {
  const common = {
    initial: { opacity: 0, y: 20, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -12, scale: 0.98 },
    transition: { duration: 0.3 }
  };

  if (step === 0) {
    return (
      <motion.div {...common} className="flex h-full items-center justify-center">
        <div className="w-full max-w-xl rounded-3xl border border-blue-400/30 bg-black/40 p-6 shadow-2xl">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-blue-300">Tracked invite</p>
              <h3 className="mt-1 text-2xl font-bold text-white">{agentName}</h3>
            </div>
            <div className="rounded-2xl bg-blue-500/15 p-4 text-blue-200">
              <Copy className="h-7 w-7" />
            </div>
          </div>
          <motion.div
            className="rounded-2xl border border-gray-700 bg-gray-950 p-4 font-mono text-sm text-gray-200"
            animate={{ boxShadow: ['0 0 0 rgba(59,130,246,0)', '0 0 32px rgba(59,130,246,0.28)', '0 0 0 rgba(59,130,246,0)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            yoursongrequest.com/signup?agent={inviteCode || 'AGENT1'}
          </motion.div>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center text-sm">
            {['Share link', 'Artist joins', 'Roster linked'].map((label, i) => (
              <motion.div
                key={label}
                className="rounded-2xl border border-gray-800 bg-gray-900/80 p-4"
                animate={{ y: i === 1 ? [-2, 2, -2] : [2, -2, 2] }}
                transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.12 }}
              >
                <CheckCircle className="mx-auto mb-2 h-5 w-5 text-green-400" />
                <p className="text-gray-300">{label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  }

  if (step === 1) {
    return (
      <motion.div {...common} className="flex h-full items-center justify-center">
        <div className="grid w-full max-w-3xl gap-4 md:grid-cols-[1fr_0.8fr]">
          <div className="rounded-3xl border border-purple-400/25 bg-gray-950 p-5">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-500/20">
                <Radio className="h-6 w-6 text-red-300" />
              </div>
              <div>
                <p className="text-sm text-red-300">LIVE NOW</p>
                <h3 className="text-xl font-bold text-white">The Night Owls</h3>
              </div>
            </div>
            <div className="space-y-3">
              {['Wonderwall', 'Mr. Brightside', 'Dancing Queen'].map((song, i) => (
                <motion.div
                  key={song}
                  className="flex items-center justify-between rounded-xl border border-gray-800 bg-black/60 px-4 py-3"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.12 * i }}
                >
                  <span className="text-white">{song}</span>
                  <span className="text-green-300">{fmtBalance(5 + i)}</span>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div
            className="rounded-[2rem] border border-gray-700 bg-black p-4 shadow-2xl"
            animate={{ y: [-6, 6, -6] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-gray-700" />
            <div className="rounded-2xl bg-gradient-to-b from-blue-950 to-gray-950 p-4">
              <Smartphone className="mb-4 h-7 w-7 text-blue-300" />
              <p className="text-sm text-gray-300">Fan scans QR</p>
              <p className="mt-2 text-2xl font-bold text-white">Request a song</p>
              <button className="mt-8 w-full rounded-xl bg-blue-600 py-3 text-sm font-bold text-white">Pay & send</button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  if (step === 2) {
    return (
      <motion.div {...common} className="flex h-full items-center justify-center">
        <div className="w-full max-w-2xl rounded-3xl border border-green-400/25 bg-gray-950 p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-green-300">Incoming requests</p>
              <h3 className="text-2xl font-bold text-white">Artist live dashboard</h3>
            </div>
            <motion.div
              className="rounded-full bg-green-500 px-3 py-1 text-sm font-bold text-black"
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            >
              PAID
            </motion.div>
          </div>
          <div className="space-y-3">
            {[
              ['Sweet Caroline', 'Sarah', 7],
              ['Valerie', 'Mike', 6],
              ['Shallow', 'Priya', 8]
            ].map(([song, fan, amount], i) => (
              <motion.div
                key={song}
                className="rounded-2xl border border-gray-800 bg-black/70 p-4"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.18 }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-white">{song}</p>
                    <p className="text-sm text-gray-500">Requested by {fan}</p>
                  </div>
                  <p className="text-lg font-bold text-green-300">{fmtBalance(amount)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div {...common} className="flex h-full items-center justify-center">
      <div className="grid w-full max-w-3xl gap-4 md:grid-cols-2">
        <div className="rounded-3xl border border-blue-400/25 bg-gray-950 p-5">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-300">Roster</p>
          <h3 className="mt-2 text-2xl font-bold text-white">4 linked artists</h3>
          <div className="mt-5 space-y-3">
            {['The Night Owls', 'Ella James Live', 'Harbour Lights'].map((name, i) => (
              <motion.div
                key={name}
                className="flex items-center justify-between rounded-xl border border-gray-800 bg-black/60 px-4 py-3"
                animate={{ x: i === 0 ? [0, 4, 0] : [0, -4, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.2 }}
              >
                <span className="text-white">{name}</span>
                <span className="text-blue-300">{i === 0 ? 'Live' : 'Ready'}</span>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-green-400/25 bg-gray-950 p-5">
          <p className="text-xs uppercase tracking-[0.25em] text-green-300">Agent earnings</p>
          <motion.p
            className="mt-3 text-5xl font-black text-white"
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          >
            {fmtBalance(316)}
          </motion.p>
          <p className="mt-2 text-sm text-gray-400">Demo balance from roster activity</p>
          <div className="mt-6 rounded-2xl bg-green-500/10 p-4 text-sm text-green-200">
            Your real dashboard fills in as artists use their YSR links at gigs.
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AgentDashboardPage;
